
import React, { useState, Suspense, Component } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography, Button, CircularProgress, Container, Paper } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { lazyWithRetry, initChunkErrorHandler } from './utils/lazyWithRetry';

// Initialize global chunk error handling
initChunkErrorHandler();

// ============================================================
// MAINTENANCE MODE CONFIGURATION
// ============================================================
// Set to true to show "Website in Entwicklung" message
// Set to false to show the normal website
const MAINTENANCE_MODE = false;
// ============================================================
import { CategoriesProvider } from './contexts/CategoriesContext';
import { LocationProvider } from './contexts/LocationContext';
import { UserProfileProvider, useUserProfile } from './contexts/UserProfileContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PricingProvider, usePricing } from './contexts/PricingContext';
import { RequestCacheProvider } from './contexts/RequestCacheContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
// Chat disabled
import { ChatProvider } from './contexts/ChatContext';
import { useAuth } from './hooks/useAuth';
import theme from './theme/theme';

// Core components (loaded immediately for critical path)
import SplashScreen from './components/SplashScreen';
import SkipNavigation from './components/SkipNavigation';
import PublicNavigationBar from './components/PublicNavigationBar';
import NavigationBar from './components/NavigationBar';
import PublicBottomNav from './components/PublicBottomNav';
import CookieConsentBanner from './components/CookieConsentBanner';
import AuthenticatedAppFooter from './components/AuthenticatedAppFooter';

// Lazy-loaded components (loaded on demand) with retry logic for chunk loading failures
const PublicHomePage = lazyWithRetry(() => import('./components/PublicHomePage'));
const HowItWorksPage = lazyWithRetry(() => import('./components/HowItWorksPage'));
const LoginDialog = lazyWithRetry(() => import('./components/LoginDialog'));
const OnboardingDialog = lazyWithRetry(() => import('./components/OnboardingDialog'));
const VerifyEmailPage = lazyWithRetry(() => import('./components/VerifyEmailPage'));
const PasswordResetPage = lazyWithRetry(() => import('./components/PasswordResetPage'));

// Authenticated user components (loaded only after login)
const PersonalizedActivityList = lazyWithRetry(() => import('./components/PersonalizedActivityList'));
const ActivitiesPage = lazyWithRetry(() => import('./components/ActivitiesPage'));
const CityActivitiesPage = lazyWithRetry(() => import('./components/CityActivitiesPage'));
const MapView = lazyWithRetry(() => import('./components/MapView'));
const ActivityDetailPage = lazyWithRetry(() => import('./components/ActivityDetailPage'));
const PublicActivityDetailPage = lazyWithRetry(() => import('./components/PublicActivityDetailPage'));
const OrganizerDetailPage = lazyWithRetry(() => import('./components/OrganizerDetailPage'));
const MyActivitiesPage = lazyWithRetry(() => import('./components/MyActivitiesPage'));
const MyParticipationsPage = lazyWithRetry(() => import('./components/MyParticipationsPage'));
const GroupManagementPage = lazyWithRetry(() => import('./components/GroupManagementPage'));
const ProfilePage = lazyWithRetry(() => import('./components/ProfilePage'));
const PricingPage = lazyWithRetry(() => import('./components/PricingPage'));
const CalendarView = lazyWithRetry(() => import('./components/CalendarView'));
// Chat disabled
// const ChatPage = lazyWithRetry(() => import('./components/chat/ChatPage'));
const CreateActivityDialog = lazyWithRetry(() => import('./components/CreateActivityDialog'));
const EnhancedAppBar = lazyWithRetry(() => import('./components/EnhancedAppBar'));
const NotificationCenter = lazyWithRetry(() => import('./components/NotificationCenter'));
import PlanActivationDialog from './components/PlanActivationDialog';
import OrganizerDetailsDialog from './components/OrganizerDetailsDialog';

// Types
import { Activity, Category } from './types/Activity';
import { CityWithActivities, CountryWithActivities, SubscriptionPlan } from './services/api';

import { useTheme, useMediaQuery } from '@mui/material';

// Utilities
import { getStoredPlan, clearStoredPlan, StoredPlanSelection, hasFreeTrialSignupIntent, clearFreeTrialSignupIntent } from './utils/planSelection';
import { trackPageView, trackEvent, trackUserAction } from './utils/analytics';

/**
 * Path → view for the simple public/app routes. Shared by initial deep-link handling and
 * popstate so the two can never drift apart.
 */
const resolveStaticRoute = (path: string, isAuthenticated: boolean): string | undefined => {
  const routes: Record<string, string> = {
    '/': isAuthenticated ? 'home' : 'public-home',
    '/home': 'home',
    '/activities': isAuthenticated ? 'activities' : 'public-activities',
    '/calendar': 'calendar',
    '/pricing': 'pricing',
    '/how-it-works': 'how-it-works',
    '/map': 'map',
    '/notifications': 'notifications',
    '/my-activities': 'my-activities',
    '/my-participations': 'my-participations',
    '/profile': 'profile',
    '/groups': 'groups',
  };
  return routes[path];
};

/**
 * /city/:country/:city — the shape getPagePath() writes and the sitemap advertises.
 * The single-segment form is kept for links created before the country was included.
 */
const parseCityPath = (path: string): { name: string; country: string } | null => {
  const withCountry = path.match(/^\/city\/([^/]+)\/([^/]+)\/?$/);
  if (withCountry) {
    return {
      country: decodeURIComponent(withCountry[1]),
      name: decodeURIComponent(withCountry[2]),
    };
  }

  const cityOnly = path.match(/^\/city\/([^/]+)\/?$/);
  if (cityOnly) {
    return { country: '', name: decodeURIComponent(cityOnly[1]) };
  }

  return null;
};

// Error boundary for lazy loaded components
class LazyLoadErrorBoundary extends Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error; isRetrying: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, isRetrying: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
    
    // Track lazy loading errors for debugging
    if (typeof window !== 'undefined') {
      // Check if this is a chunk loading error (includes "Unexpected token '<'" for HTML returned as JS)
      const isChunkError = 
        error.message.includes('Loading chunk') || 
        error.message.includes('expected expression') ||
        error.message.includes("Unexpected token '<'") ||
        error.message.includes('dynamically imported module') ||
        error.message.includes('Failed to fetch dynamically');
        
      if (isChunkError) {
        console.error('🚨 Chunk loading error detected. This may be due to:');
        console.error('   1. New deployment with changed chunk file names');
        console.error('   2. Service Worker serving old/wrong content');
        console.error('   3. Server returning HTML instead of JS');
        console.error('💡 Attempting automatic recovery...');
        
        // Attempt automatic recovery by clearing caches
        this.clearCachesAndReload();
      }
    }
  }

  clearCachesAndReload = async () => {
    this.setState({ isRetrying: true });
    
    try {
      // 1. Clear service worker caches
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          if (registration.active) {
            console.log('🔄 Sending cache clear message to service worker');
            const messageChannel = new MessageChannel();
            registration.active.postMessage({ type: 'CLEAR_CACHE' }, [messageChannel.port2]);
          }
        }
      }
      
      // 2. Clear all caches via Cache API
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => {
          console.log('🗑️ Deleting cache:', cacheName);
          return caches.delete(cacheName);
        }));
        console.log('✅ All caches cleared');
      }
      
      // 3. Force reload from server (bypass cache)
      console.log('🔄 Reloading page from server...');
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to clear caches:', error);
      this.setState({ isRetrying: false });
    }
  };

  handleManualRetry = () => {
    // Simple retry: clear state and let React re-render
    this.setState({ hasError: false, error: undefined, isRetrying: false });
  };

  handleHardRefresh = () => {
    this.clearCachesAndReload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isRetrying) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              gap: 2,
              p: 3,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary">
              Clearing cache and reloading...
            </Typography>
          </Box>
        );
      }
      
      return this.props.fallback || (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            gap: 2,
            p: 3,
          }}
        >
          <Typography variant="h6" color="error">
            Loading failed
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
            A new version may be available. Click below to refresh.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={this.handleManualRetry}
            >
              Try Again
            </Button>
            <Button 
              variant="contained" 
              onClick={this.handleHardRefresh}
            >
              Clear Cache & Reload
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Loading component for Suspense
const LoadingFallback: React.FC<{ message?: string }> = ({ message = "Loading..." }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      gap: 2,
    }}
  >
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

const AppContent: React.FC = () => {
  const { user, loading, needsOnboarding, completeOnboarding, isAuthenticated } = useAuth();
  const { subscriptionStatus } = useUserProfile();
  const { pricingData } = usePricing();
  const isPricingHidden = pricingData?.pricing_hidden === true;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // App state - Start with public-home for unauthenticated users
  // Use a ref to track initial view setting to prevent flickering
  const initialViewSet = React.useRef(false);
  const [currentView, setCurrentView] = useState('public-home');
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedOrganizer, setSelectedOrganizer] = useState<{ userId: string; name: string } | null>(null);
  const [organizerDialogOpen, setOrganizerDialogOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<CityWithActivities | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryWithActivities | null>(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showCreateActivityDialog, setShowCreateActivityDialog] = useState(false);
  const loginDialogOpenRef = React.useRef(false); // Track dialog state to prevent loops
  const [notificationSource, setNotificationSource] = useState<{activityId: number; fromNotification: boolean} | null>(null);
  const [isNavigatingFromNotification, setIsNavigatingFromNotification] = useState(false);
  const navigationFlagRef = React.useRef(false); // More reliable flag using useRef
  
  // Public activity viewing state
  const [publicActivityId, setPublicActivityId] = useState<number | null>(null);
  
  // Plan activation state
  const [showPlanActivationDialog, setShowPlanActivationDialog] = useState(false);
  const [selectedPlanForActivation, setSelectedPlanForActivation] = useState<StoredPlanSelection | null>(null);

  // Handle user authentication state changes
  React.useEffect(() => {
    if (!loading) {
      if (isAuthenticated) {
        // User is authenticated - close login dialog if open and switch views
        if (showLoginDialog) {
          console.log('🔐 User authenticated, closing login dialog');
          loginDialogOpenRef.current = false;
          setShowLoginDialog(false);
        }
        
        if (currentView.startsWith('public-')) {
          // User logged in while on public page - switch to authenticated equivalent
          if (!initialViewSet.current) {
            setCurrentView(currentView.replace('public-', ''));
            initialViewSet.current = true;
          }
        }
      } else if (
        !isAuthenticated &&
        !currentView.startsWith('public-') &&
        currentView !== 'home' &&
        currentView !== 'pricing' &&
        currentView !== 'how-it-works' &&
        currentView !== 'calendar' &&
        // City hubs render for signed-out visitors too (renderPublicContent handles them) and
        // are advertised in the sitemap — bouncing them to public-home would soft-404 the URL.
        currentView !== 'city-activities' &&
        currentView !== 'verify-email' &&
        currentView !== 'reset-password'
      ) {
        // User logged out - switch to public equivalent or public home
        // Exclude how-it-works and calendar since they're already public views
        if (!initialViewSet.current) {
          const publicViews: Record<string, string> = {
            'activities': 'public-activities',
            'home': 'public-home'
          };
          setCurrentView(publicViews[currentView] || 'public-home');
          initialViewSet.current = true;
        }
      }
    }
  }, [isAuthenticated, loading, showLoginDialog, currentView]);

  // Authenticated + shared: navigate to pricing from custom events (e.g. social upgrade CTAs)
  React.useEffect(() => {
    const goPricing = () => setCurrentView('pricing');
    window.addEventListener('navigateToPricing', goPricing);
    return () => window.removeEventListener('navigateToPricing', goPricing);
  }, []);

  React.useEffect(() => {
    const openLogin = () => setShowLoginDialog(true);
    window.addEventListener('openLoginDialog', openLogin);
    return () => window.removeEventListener('openLoginDialog', openLogin);
  }, []);

  // Browse activities from home CTAs (e.g. empty members strip)
  React.useEffect(() => {
    const goActivities = () => setCurrentView(user ? 'activities' : 'public-activities');
    window.addEventListener('navigateToActivities', goActivities);
    return () => window.removeEventListener('navigateToActivities', goActivities);
  }, [user]);

  // Pricing wall (member profile) — open checkout for chosen plan
  React.useEffect(() => {
    const openActivation = (e: Event) => {
      const ce = e as CustomEvent<{ plan: SubscriptionPlan }>;
      const plan = ce.detail?.plan;
      if (!plan) return;
      const sel: StoredPlanSelection = {
        plan,
        timestamp: Date.now(),
        source: 'member_profile',
      };
      setSelectedPlanForActivation(sel);
      setShowPlanActivationDialog(true);
    };
    window.addEventListener('openPlanActivationWithPlan', openActivation as EventListener);
    return () => window.removeEventListener('openPlanActivationWithPlan', openActivation as EventListener);
  }, []);

  // Handle deep links on app initialization (supports path-based and legacy query-based)
  React.useEffect(() => {
    const handleDeepLink = () => {
      const path = window.location.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      const activityIdParam = urlParams.get('activity');
      
      // Check for Firebase action links (email verification or password reset)
      // IMPORTANT: Check for oobCode parameter first, before checking paths
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      // Priority 1: If we have an oobCode, this is definitely a Firebase action link
      if (oobCode) {
        if (mode === 'verifyEmail') {
          console.log('📧 Email verification link detected (mode=verifyEmail)');
          setCurrentView('verify-email');
          return;
        } else if (mode === 'resetPassword') {
          console.log('🔒 Password reset link detected (mode=resetPassword)');
          setCurrentView('reset-password');
          return;
        }
        // If oobCode exists but mode is unrecognized, still try to determine by path
        if (path === '/verify-email' || path.includes('verify-email')) {
          console.log('📧 Email verification link detected (by path)');
          setCurrentView('verify-email');
          return;
        } else if (path === '/reset-password' || path.includes('reset-password')) {
          console.log('🔒 Password reset link detected (by path)');
          setCurrentView('reset-password');
          return;
        }
      }

      // Prefer modern path-based deep links: /activity/:id
      const activityMatch = path.match(/^\/activity\/(\d+)$/);
      if (activityMatch) {
        const id = Number(activityMatch[1]);
        if (!isNaN(id)) {
          if (user) {
            setSelectedActivity({ id } as Activity);
            setCurrentView('activity-detail');
          } else {
            setPublicActivityId(id);
            setCurrentView('public-activity-detail');
          }
          return;
        }
      }

      // City hub deep links: /city/DE/Braunschweig
      const cityFromPath = parseCityPath(path);
      if (cityFromPath) {
        setSelectedCity({
          name: cityFromPath.name,
          country: cityFromPath.country,
          activity_count: 0,
          image_url: '',
        });
        setCurrentView('city-activities');
        return;
      }

      // Handle simple static routes
      const staticView = resolveStaticRoute(path, !!user);
      if (staticView) {
        setCurrentView(staticView);
        return;
      }

      // Backward-compatibility: legacy query param deep link
      if (activityIdParam && !isNaN(Number(activityIdParam))) {
        const id = Number(activityIdParam);
        if (user) {
          setSelectedActivity({ id } as Activity);
          setCurrentView('activity-detail');
        } else {
          setPublicActivityId(id);
          setCurrentView('public-activity-detail');
        }
      }
    };

    if (!loading) {
      handleDeepLink();
    }
  }, [loading, user]);

  // Handle browser back/forward buttons with path-based routing
  React.useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const path = window.location.pathname;
      const activityMatch = path.match(/^\/activity\/(\d+)$/);

      if (activityMatch) {
        const id = Number(activityMatch[1]);
        if (!isNaN(id)) {
          setSelectedActivity({ id } as Activity);
          setCurrentView('activity-detail');
          if ((event.state as { fromNotification?: boolean })?.fromNotification) {
            setNotificationSource({ activityId: id, fromNotification: true });
          }
          return;
        }
      }

      const cityFromPath = parseCityPath(path);
      if (cityFromPath) {
        setSelectedCity({
          name: cityFromPath.name,
          country: cityFromPath.country,
          activity_count: 0,
          image_url: '',
        });
        setCurrentView('city-activities');
        setSelectedActivity(null);
        setSelectedCategory(null);
        setSelectedOrganizer(null);
        setNotificationSource(null);
        return;
      }

      const view = resolveStaticRoute(path, !!user) || (user ? 'home' : 'public-home');
      setCurrentView(view);
      setSelectedActivity(null);
      setSelectedCategory(null);
      setSelectedOrganizer(null);
      setNotificationSource(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

  // Check for stored plan selection or free trial signup intent after user login
  React.useEffect(() => {
    if (user && !loading && !needsOnboarding) {
      // Check for free trial signup intent first
      if (hasFreeTrialSignupIntent()) {
        console.log('🎯 Found free trial signup intent after login, redirecting to home');
        clearFreeTrialSignupIntent();
        setCurrentView('home');
        return;
      }
      
      // Check for stored paid plan selection
      const storedPlan = getStoredPlan();
      const isUnlimited = subscriptionStatus === 'team_member' || subscriptionStatus === 'global_free_access';
      if (storedPlan && !isPricingHidden && !isUnlimited) {
        console.log('🎯 Found stored plan after login:', storedPlan.plan.display_name);
        setSelectedPlanForActivation(storedPlan);
        setShowPlanActivationDialog(true);
      }
    }
  }, [user, loading, needsOnboarding, isPricingHidden, subscriptionStatus]);

  // Track page views and synchronize the address bar when view (or its identifiers) changes
  React.useEffect(() => {
    if (!loading && currentView) {
      const urlParams = new URLSearchParams(window.location.search);
      const hasActionCode = urlParams.has('oobCode') && urlParams.get('mode');

      // If we have a Firebase action code in the URL but haven't switched views yet,
      // wait to avoid stripping query parameters before deep link handling runs.
      if (
        hasActionCode &&
        currentView !== 'verify-email' &&
        currentView !== 'reset-password'
      ) {
        return;
      }

      const pageTitle = getPageTitle(currentView);
      const pagePath = getPagePath(currentView);
      const origin = window.location.origin;
      const currentFullPath = window.location.pathname + window.location.search;
      const shouldPreserveActionParams =
        (currentView === 'verify-email' || currentView === 'reset-password') && hasActionCode;
      // The address bar already points at this view — leave it alone so page-owned query
      // params survive (e.g. /activities?q=yoga&city=Braunschweig from a shared or indexed link).
      const pathAlreadyMatchesView = window.location.pathname === pagePath;

      if (!shouldPreserveActionParams && !pathAlreadyMatchesView && currentFullPath !== pagePath) {
        window.history.pushState({ view: currentView }, pageTitle, pagePath);
      }

      const pageUrl = shouldPreserveActionParams
        ? window.location.href
        : `${origin}${pagePath}`;

      // Track page view
      trackPageView(pageUrl, pageTitle);
      
      // Track user navigation event
      trackEvent('page_view', 'navigation', currentView);
      
      console.log('📊 Analytics: Page view tracked -', currentView, pageTitle);
    }
  }, [currentView, loading, selectedActivity, publicActivityId, selectedOrganizer, selectedCity]);

  // Helper function to get page titles for analytics
  const getPageTitle = (view: string): string => {
    switch (view) {
      case 'home': return 'Home - Personalized Activities';
      case 'public-home': return 'Discover Activities - TreffPunkts';
      case 'activities': return 'Browse Activities';
      case 'public-activities': return 'Browse Activities - Join Now';
      case 'search-results': return 'Search Results';
      case 'calendar': return 'Calendar View';
      case 'map': return 'Map View';
      case 'activity-detail': return selectedActivity ? `Activity: ${selectedActivity.title || 'Details'}` : 'Activity Details';
      case 'public-activity-detail': return publicActivityId ? `Activity ${publicActivityId} - Join Now` : 'Activity Details';
      case 'my-activities': return 'My Activities';
      case 'my-participations': return 'My Participations';
      case 'profile': return 'Profile';
      // Chat disabled
      // case 'chat': return 'My Chats';
      case 'organizer-detail': return selectedOrganizer ? `Organizer: ${selectedOrganizer.name}` : 'Organizer Profile';
      case 'city-activities': return selectedCity ? `${selectedCity.name} Activities` : 'City Activities';
      case 'pricing': return 'Pricing Plans';
      case 'how-it-works': return 'How It Works - Learn About TreffPunkts';
      case 'groups': return 'Group Management';
      case 'notifications': return 'Notifications Center';
      case 'verify-email': return 'Verify Email - TreffPunkts';
      case 'reset-password': return 'Reset Password - TreffPunkts';
      default: return 'TreffPunkts';
    }
  };

  // Helper function to build a virtual path for GA page_path/page_location
  const getPagePath = (view: string): string => {
    switch (view) {
      case 'home': return '/home';
      case 'public-home': return '/';
      case 'activities':
      case 'public-activities': return '/activities';
      case 'search-results': return '/search';
      case 'calendar': return '/calendar';
      case 'map': return '/map';
      case 'activity-detail': {
        const id = selectedActivity?.id ? String(selectedActivity.id) : '';
        return id ? `/activity/${id}` : '/activity';
      }
      case 'public-activity-detail': {
        const id = publicActivityId ? String(publicActivityId) : '';
        return id ? `/activity/${id}` : '/activity';
      }
      case 'my-activities': return '/my-activities';
      case 'my-participations': return '/my-participations';
      case 'profile': return '/profile';
      // Chat disabled
      // case 'chat': return '/chat';
      case 'organizer-detail': {
        const organizerId = selectedOrganizer?.userId || '';
        return organizerId ? `/organizer/${encodeURIComponent(organizerId)}` : '/organizer';
      }
      case 'city-activities': {
        const cityName = selectedCity?.name ? encodeURIComponent(selectedCity.name) : '';
        const country = selectedCity?.country ? encodeURIComponent(selectedCity.country) : '';
        if (cityName && country) return `/city/${country}/${cityName}`;
        if (cityName) return `/city/${cityName}`;
        return '/city';
      }
      case 'pricing': return '/pricing';
      case 'how-it-works': return '/how-it-works';
      case 'groups': return '/groups';
      case 'notifications': return '/notifications';
      case 'verify-email': return '/verify-email';
      case 'reset-password': return '/reset-password';
      default: return '/';
    }
  };

  // Show splash screen while loading
  if (loading) {
    return <SplashScreen />;
  }

  // Show onboarding for new users
  if (user && needsOnboarding) {
    return (
      <OnboardingDialog
        open={true}
        onComplete={completeOnboarding}
        initialData={{
          email: user.email || '',
          name: user.displayName || '',
        }}
      />
    );
  }

  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedActivity(null);
    setSelectedCategory(null);
    setSelectedOrganizer(null);
    setNotificationSource(null);
    setIsNavigatingFromNotification(false);
    setPublicActivityId(null);
    navigationFlagRef.current = false; // Also reset ref flag
    // URL will be synchronized by the page view effect
  };

  const handleCategoryIntentConsumed = () => {
    setSelectedCategory(null);
  };

  const handleCategorySelect = (category: Category | null) => {
    setSelectedCategory(category);
    // Clear any previous city intent so filters don't stack unintentionally
    setSelectedCity(null);
    // Navigate to public/auth activities while preserving initial filter intent
    setCurrentView(user ? 'activities' : 'public-activities');
    
    // Track category selection
    if (category) {
      trackEvent('category_select', 'engagement', category.name, category.id);
      trackUserAction('category_browsed', {
        categoryId: category.id,
        categoryName: category.name
      });
    }
  };

  const handleOrganizerSelect = (organizer: { userId: string; name: string }) => {
    setSelectedOrganizer(organizer);
    setOrganizerDialogOpen(true);
  };

  const organizerDetailsDialog = (
    <OrganizerDetailsDialog
      open={organizerDialogOpen && !!selectedOrganizer}
      onClose={() => {
        setOrganizerDialogOpen(false);
        setSelectedOrganizer(null);
      }}
      organizerId={selectedOrganizer?.userId || ''}
      organizerName={selectedOrganizer?.name || 'Organizer'}
    />
  );

  const handleCitySelect = (city: CityWithActivities) => {
    console.log('🏙️ City selected for exploration:', city);
    setSelectedCity(city);
    setSelectedCountry(null); // Clear country selection when city is selected
    // Clear any previous category intent so filters don't stack unintentionally
    setSelectedCategory(null);
    // Navigate to unified list for consistency; dedicated City page remains accessible
    setCurrentView(user ? 'activities' : 'public-activities');
    
    // Track city selection
    trackEvent('city_select', 'exploration', `${city.name}, ${city.country}`, city.activity_count);
    trackUserAction('city_explored', {
      cityName: city.name,
      country: city.country,
      activityCount: city.activity_count,
      source: currentView
    });
  };

  const handleCountrySelect = (country: CountryWithActivities) => {
    console.log('🌍 Country selected for exploration:', country);
    setSelectedCountry(country);
    setSelectedCity(null); // Clear city selection when country is selected
    // Clear any previous category intent so filters don't stack unintentionally
    setSelectedCategory(null);
    // Navigate to unified list for consistency
    setCurrentView(user ? 'activities' : 'public-activities');
    
    // Track country selection
    trackEvent('country_select', 'exploration', `${country.country_name} (${country.country_code})`, country.activity_count);
    trackUserAction('country_explored', {
      countryName: country.country_name,
      countryCode: country.country_code,
      activityCount: country.activity_count,
      source: currentView
    });
  };

  const handleCalendarView = () => {
    setCurrentView('calendar');
    setSelectedCategory(null);
  };

  const handleNotificationsView = () => {
    setCurrentView('notifications');
  };

  const handleMapView = () => {
    console.log('🗺️ Analytics: Map view triggered');
    setCurrentView('map');
    setSelectedActivity(null);
    setSelectedCategory(null);
    
    // Explicit analytics tracking for map view
    trackEvent('map_view', 'navigation', 'map_opened');
    trackUserAction('view_map', { source: currentView });
    
    // Ensure GTM tracking for tag coverage
    setTimeout(async () => {
      try {
        const { ensureMapPageTracking } = await import('./utils/gtmVerification');
        ensureMapPageTracking();
      } catch (error) {
        console.error('❌ Failed to ensure map tracking:', error);
      }
    }, 500);
  };

  const handleActivitySelect = (activity: Activity) => {
    if (user) {
      // User is authenticated - show full activity detail page
      setSelectedActivity(activity);
      setCurrentView('activity-detail');
    } else {
      // User is not authenticated - show public activity detail page
      setPublicActivityId(activity.id);
      setCurrentView('public-activity-detail');
    }
    
    // URL will be synchronized by the page view effect
    
    // Track activity selection
    trackEvent('activity_select', 'engagement', `Activity ID: ${activity.id}`, activity.id);
    trackUserAction('activity_viewed', {
      activityId: activity.id,
      activityTitle: activity.title,
      categoryId: activity.category_id,
      source: currentView,
      isAuthenticated: !!user
    });
  };


  const handleCreateActivity = () => {
    setShowCreateActivityDialog(true);
    
    // Track create activity intent
    trackEvent('create_activity_start', 'engagement', 'create_button_clicked');
    trackUserAction('create_activity_initiated', {
      source: currentView,
      timestamp: new Date().toISOString()
    });
  };

  const handleActivityCreated = () => {
    setShowCreateActivityDialog(false);
    // Optionally refresh the current view
    if (currentView === 'home') {
      // Trigger refresh of home activities
    }
    
    // Track activity creation completion
    trackEvent('create_activity_completed', 'engagement', 'dialog_closed_success');
    trackUserAction('activity_creation_flow_completed', {
      source: currentView,
      timestamp: new Date().toISOString()
    });
  };


  const handleNavigateToMyParticipations = () => {
    setCurrentView('my-participations');
  };

  const handleLogin = () => {
    // Prevent opening dialog if it's already open or if user is already authenticated
    if (loginDialogOpenRef.current || isAuthenticated) {
      console.log('🚫 Login dialog already open or user authenticated, skipping');
      return;
    }
    
    loginDialogOpenRef.current = true;
    setShowLoginDialog(true);
    
    // Track login intent
    trackEvent('login_start', 'authentication', 'login_clicked');
    trackUserAction('login_initiated', {
      source: currentView,
      timestamp: new Date().toISOString()
    });
  };

  const handleCloseLoginDialog = () => {
    loginDialogOpenRef.current = false; // Reset the ref when dialog closes
    setShowLoginDialog(false);
    
    // Track login dialog closure
    trackEvent('login_dialog_closed', 'authentication', 'dialog_closed');
    trackUserAction('login_dialog_dismissed', {
      source: currentView,
      timestamp: new Date().toISOString()
    });
  };

  const handlePublicActivityLogin = () => {
    // Prevent opening dialog if it's already open or if user is already authenticated
    if (loginDialogOpenRef.current || isAuthenticated) {
      console.log('🚫 Login dialog already open or user authenticated, skipping public activity login');
      return;
    }
    
    loginDialogOpenRef.current = true;
    setShowLoginDialog(true);
    // Track conversion from public activity page
    trackEvent('login_from_public_activity', 'conversion', publicActivityId?.toString() || '');
  };

  const handlePlanActivationSuccess = () => {
    console.log('✅ Plan activation successful, clearing stored plan');
    clearStoredPlan();
    setSelectedPlanForActivation(null);
    setShowPlanActivationDialog(false);
    
    // Optionally navigate to profile page to see subscription details
    setCurrentView('profile');
  };

  const handleClosePlanActivationDialog = () => {
    console.log('❌ Plan activation cancelled, clearing stored plan');
    clearStoredPlan();
    setSelectedPlanForActivation(null);
    setShowPlanActivationDialog(false);
    
    // Redirect user to home page so they can browse the app
    console.log('🏠 Redirecting to home page after plan activation cancellation');
    setCurrentView('home');
  };

  // Temporarily disabled notification handlers to fix render loop
  /*
  const handleNotificationClick = React.useCallback((notificationData: NotificationData) => {
    // Navigate to activity detail screen
    if (notificationData.activity_id) {
      const activityId = parseInt(notificationData.activity_id);
      setSelectedActivity({ id: activityId } as Activity); // We'll load full activity data in ActivityDetailPage
      setCurrentView('activity-detail');
      
      // Show smart notification dialog
      setSelectedNotificationData(notificationData);
      setNotificationDetailOpen(true);
    }
  }, []); // Empty dependency array since this function doesn't depend on any props or state

  const handleCloseNotificationDetail = React.useCallback(() => {
    setNotificationDetailOpen(false);
    setSelectedNotificationData(null);
  }, []);
  */

  // Handle navigation from notifications
  const handleNotificationNavigation = (activityId: number) => {
    console.log('📍 Navigating to activity from notification:', activityId);
    console.log('🔄 Setting navigation flag to TRUE');
    setIsNavigatingFromNotification(true);
    navigationFlagRef.current = true; // Also set ref flag
    setSelectedActivity({ id: activityId } as Activity);
    setNotificationSource({ activityId, fromNotification: true });
    console.log('🔄 Setting view to activity-detail');
    setCurrentView('activity-detail');
    
    // URL will be synchronized by the page view effect
    
    // Reset navigation flag after a longer delay to ensure dialog close completes
    setTimeout(() => {
      console.log('🔄 Resetting navigation flag to FALSE after 500ms');
      setIsNavigatingFromNotification(false);
      navigationFlagRef.current = false; // Also reset ref flag
    }, 500); // Increased from 200ms to 500ms
  };

  const handleFooterPricingNavigation = () => {
    setCurrentView('pricing');
  };

  // Render navigation based on screen size
  const renderNavigation = () => {
    if (isMobile) {
      // Mobile: Use bottom navigation
      return (
        <NavigationBar
          currentView={currentView}
          onViewChange={setCurrentView}
          onMapView={handleMapView}
          onCreateActivity={() => setCurrentView('create-activity')}
          onActivitySelect={handleActivitySelect}
        />
      );
    }
    
    // Desktop: Use top app bar
    return (
      <EnhancedAppBar
        onProfileOpen={() => setCurrentView('profile')}
        onMapView={handleMapView}
        onHomeView={() => setCurrentView('home')}
        onActivitiesView={() => setCurrentView('activities')}
        onCalendarView={() => setCurrentView('calendar')}
        onMyActivitiesView={() => setCurrentView('my-activities')}
        onMyParticipationsView={() => setCurrentView('my-participations')}
        onActivitySelect={handleActivitySelect}
        currentView={currentView}
      />
    );
  };

  const renderPublicContent = () => {
    switch (currentView) {
      case 'public-home':
        return (
          <Suspense fallback={<LoadingFallback message="Loading homepage..." />}>
            <PublicHomePage
              onActivitySelect={handleActivitySelect}
              onCategorySelect={handleCategorySelect}
              onCitySelect={handleCitySelect}
              onCountrySelect={handleCountrySelect}
              onViewAllActivities={() => setCurrentView('public-activities')}
              onHowItWorks={() => setCurrentView('how-it-works')}
              onLogin={handleLogin}
            />
          </Suspense>
        );

      case 'how-it-works':
        console.log('🔄 Rendering HowItWorksPage');
        try {
          return (
            <HowItWorksPage
              onBack={() => {
                console.log('🔄 HowItWorksPage onBack called');
                setCurrentView('public-home');
              }}
              onLogin={handleLogin}
            />
          );
        } catch (error) {
          console.error('❌ Error rendering HowItWorksPage:', error);
        return (
          <PublicHomePage
              onActivitySelect={handleActivitySelect}
              onCategorySelect={handleCategorySelect}
              onCitySelect={handleCitySelect}
              onCountrySelect={handleCountrySelect}
              onViewAllActivities={() => setCurrentView('public-activities')}
              onHowItWorks={() => setCurrentView('how-it-works')}
              onLogin={handleLogin}
            />
          );
        }

      case 'verify-email':
        return (
          <Suspense fallback={<LoadingFallback message="Verifying email..." />}>
            <VerifyEmailPage
              onVerificationComplete={() => {
                console.log('✅ Email verification completed, showing login dialog');
                setCurrentView('public-home');
                setShowLoginDialog(true);
              }}
            />
          </Suspense>
        );

      case 'reset-password':
        return (
          <Suspense fallback={<LoadingFallback message="Resetting password..." />}>
            <PasswordResetPage
              onResetComplete={() => {
                console.log('✅ Password reset completed, showing login dialog');
                setCurrentView('public-home');
                setShowLoginDialog(true);
              }}
            />
          </Suspense>
        );

      case 'public-activities':
        return (
          <Box sx={{ 
            pt: { xs: 8, md: 10 }, 
            pb: { xs: 2, md: 3 },
            minHeight: '100vh',
            bgcolor: 'background.default',
            width: '100%',
            overflowX: 'hidden'
          }}>
            <ActivitiesPage 
              onActivitySelect={handleActivitySelect}
              initialCategoryId={selectedCategory?.id}
              initialCity={selectedCity?.name}
              initialCountry={selectedCountry?.country_code || selectedCity?.country}
              onInitialFiltersConsumed={handleCategoryIntentConsumed}
            />
          </Box>
        );

      case 'calendar':
        return (
          <Box sx={{ height: '100vh', overflow: 'hidden' }}>
            <CalendarView
              onActivitySelect={handleActivitySelect}
              onBack={() => setCurrentView('public-home')}
              selectedCategory={selectedCategory}
              selectedCity={selectedCity?.name}
              selectedCountry={selectedCity?.country}
            />
          </Box>
        );

      case 'public-activity-detail':
        return publicActivityId ? (
          <PublicActivityDetailPage
            activityId={publicActivityId}
            onBack={() => setCurrentView('public-home')}
            onLogin={handlePublicActivityLogin}
          />
        ) : (
          <PublicHomePage
            onActivitySelect={handleActivitySelect}
            onCategorySelect={handleCategorySelect}
            onCitySelect={handleCitySelect}
            onCountrySelect={handleCountrySelect}
            onViewAllActivities={() => setCurrentView('public-activities')}
            onLogin={handleLogin}
          />
        );

      case 'city-activities':
        return selectedCity ? (
          // Same top padding as the other public views: the public app bar is fixed, and this
          // view is reachable directly via /city/:country/:city.
          <Box sx={{ pt: { xs: 8, md: 10 }, minHeight: '100vh', width: '100%', overflowX: 'hidden' }}>
            <CityActivitiesPage
              city={selectedCity}
              onBack={() => setCurrentView('public-home')}
              onActivitySelect={handleActivitySelect}
            />
          </Box>
        ) : (
          <PublicHomePage
            onActivitySelect={handleActivitySelect}
            onCategorySelect={handleCategorySelect}
            onCitySelect={handleCitySelect}
            onCountrySelect={handleCountrySelect}
            onViewAllActivities={() => setCurrentView('public-activities')}
            onLogin={handleLogin}
          />
        );

      case 'pricing':
        return (
          <PricingPage 
            onBack={() => setCurrentView('public-home')} 
            onLogin={handleLogin}
          />
        );

      default:
        return (
          <PublicHomePage
            onActivitySelect={handleActivitySelect}
            onCategorySelect={handleCategorySelect}
            onCitySelect={handleCitySelect}
            onCountrySelect={handleCountrySelect}
            onViewAllActivities={() => setCurrentView('public-activities')}
            onLogin={handleLogin}
          />
        );
    }
  };

  const renderMainContent = () => {
    // Show public navigation and content for non-authenticated users
    if (!user) {
      return (
        <>
          {/* WCAG 1.3.1 - Navigation landmark */}
          {!isMobile && currentView !== 'public-activity-detail' && currentView !== 'calendar' && (
            <Box component="header" id="main-navigation" tabIndex={-1}>
              <PublicNavigationBar
                currentView={currentView}
                onViewChange={setCurrentView}
                onLogin={handleLogin}
              />
            </Box>
          )}
          
          {/* WCAG 1.3.1 - Main content landmark */}
          <Box component="main" role="main" id="main-content" sx={{ minHeight: '50vh' }}>
            {renderPublicContent()}
          </Box>

          {/* Listen for footer-triggered pricing navigation via a window event */}
          <Box component="div" sx={{ display: 'none' }}>
            {(() => {
              // Attach once per render cycle
              const win = window as Window & { __pricingListenerAttached?: boolean };
              if (!win.__pricingListenerAttached) {
                win.__pricingListenerAttached = true;
                window.addEventListener('navigateToPricing', () => setCurrentView('pricing'));
              }
              return null;
            })()}
          </Box>
          <PublicBottomNav
            currentView={currentView}
            onViewChange={setCurrentView}
            onLogin={handleLogin}
          />
          
          {/* Always render dialogs for non-authenticated users */}
          <LoginDialog 
            open={showLoginDialog} 
            onClose={handleCloseLoginDialog}
          />
          {selectedPlanForActivation && (
            <PlanActivationDialog
              open={showPlanActivationDialog}
              onClose={handleClosePlanActivationDialog}
              planSelection={selectedPlanForActivation}
              onActivationSuccess={handlePlanActivationSuccess}
            />
          )}
        </>
      );
    }

    // Show main app for authenticated users
    switch (currentView) {
      case 'home':
        return (
        <>
          {/* WCAG 1.3.1 - Main content landmark */}
          <Box 
            component="main" 
            role="main" 
            id="main-content"
            sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              bgcolor: 'background.default',
              width: '100%',
            }}
          >
            <PersonalizedActivityList
              onCategorySelect={handleCategorySelect}
              onOrganizerSelect={handleOrganizerSelect}
              onActivitySelect={handleActivitySelect}
              onCitySelect={handleCitySelect}
              onCountrySelect={handleCountrySelect}
              onCalendarView={handleCalendarView}
              onNotificationsView={handleNotificationsView}
              onMyActivitiesView={() => setCurrentView('my-activities')}
              onCreateActivity={handleCreateActivity}
            />
          </Box>
          <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
          {/* WCAG 1.3.1 - Navigation landmark */}
          <Box component="nav" role="navigation" id="main-navigation" tabIndex={-1}>
            {renderNavigation()}
          </Box>
          {organizerDetailsDialog}
        </>
        );

      case 'activities':
        return (
          <>
            {/* WCAG 1.3.1 - Main content landmark */}
            <Box 
              component="main" 
              role="main" 
              id="main-content"
              sx={{ 
                pt: { xs: 4, md: 10 }, 
                pb: { xs: 12, md: 3 },
                minHeight: '100vh',
                width: '100%',
                overflowX: 'hidden'
              }}
            >
              <ActivitiesPage 
                onActivitySelect={handleActivitySelect}
                initialCategoryId={selectedCategory?.id}
                initialCity={selectedCity?.name}
                initialCountry={selectedCountry?.country_code || selectedCity?.country}
                onInitialFiltersConsumed={handleCategoryIntentConsumed}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {/* WCAG 1.3.1 - Navigation landmark */}
            <Box component="nav" role="navigation" id="main-navigation" tabIndex={-1}>
              {renderNavigation()}
            </Box>
            {organizerDetailsDialog}
          </>
        );

      case 'search-results':
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <ActivitiesPage onActivitySelect={handleActivitySelect} onInitialFiltersConsumed={handleCategoryIntentConsumed} />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'calendar':
        return (
          <>
            <Box sx={{ height: '100vh', overflow: 'hidden' }}>
              <CalendarView
                onActivitySelect={handleActivitySelect}
                onBack={handleBackToHome}
                selectedCategory={selectedCategory}
                selectedCity={selectedCity?.name}
                selectedCountry={selectedCity?.country}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'map':
        return (
          <>
            <Box sx={{ height: '100vh', overflow: 'hidden' }}>
              <MapView 
                onActivitySelect={handleActivitySelect}
                selectedCategory={selectedCategory}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'activity-detail':
        return selectedActivity ? (
          <>
            <ActivityDetailPage
              activityId={selectedActivity.id}
              onBack={handleBackToHome}
              onActivitySelect={handleActivitySelect}
              onNavigateToMyActivities={() => setCurrentView('my-activities')}
              onNavigateToMyParticipations={handleNavigateToMyParticipations}
              onOrganizerSelect={handleOrganizerSelect}
              fromNotification={notificationSource?.fromNotification || false}
            />
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
          </>
        ) : (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <PersonalizedActivityList
                onCategorySelect={handleCategorySelect}
                onOrganizerSelect={handleOrganizerSelect}
                onActivitySelect={handleActivitySelect}
                onCountrySelect={handleCountrySelect}
                onCalendarView={handleCalendarView}
                onNotificationsView={handleNotificationsView}
                onMyActivitiesView={() => setCurrentView('my-activities')}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'public-activity-detail':
        return publicActivityId ? (
          <>
            <PublicActivityDetailPage
              activityId={publicActivityId}
              onBack={handleBackToHome}
              onLogin={handlePublicActivityLogin}
            />
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
          </>
        ) : (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <PersonalizedActivityList
                onCategorySelect={handleCategorySelect}
                onOrganizerSelect={handleOrganizerSelect}
                onActivitySelect={handleActivitySelect}
                onCountrySelect={handleCountrySelect}
                onCalendarView={handleCalendarView}
                onNotificationsView={handleNotificationsView}
                onMyActivitiesView={() => setCurrentView('my-activities')}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'organizer-detail':
        return selectedOrganizer ? (
          <>
            <OrganizerDetailPage
              organizerId={selectedOrganizer.userId}
              onBack={handleBackToHome}
              onActivitySelect={handleActivitySelect}
            />
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
          </>
        ) : (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <PersonalizedActivityList
                onCategorySelect={handleCategorySelect}
                onOrganizerSelect={handleOrganizerSelect}
                onActivitySelect={handleActivitySelect}
                onCountrySelect={handleCountrySelect}
                onCalendarView={handleCalendarView}
                onNotificationsView={handleNotificationsView}
                onMyActivitiesView={() => setCurrentView('my-activities')}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'city-activities':
        return selectedCity ? (
          <>
            <CityActivitiesPage
              city={selectedCity}
              onBack={handleBackToHome}
              onActivitySelect={handleActivitySelect}
            />
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
          </>
        ) : (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              bgcolor: 'background.default',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <PersonalizedActivityList
                onCategorySelect={handleCategorySelect}
                onOrganizerSelect={handleOrganizerSelect}
                onActivitySelect={handleActivitySelect}
                onCitySelect={handleCitySelect}
                onCountrySelect={handleCountrySelect}
                onCalendarView={handleCalendarView}
                onNotificationsView={handleNotificationsView}
                onMyActivitiesView={() => setCurrentView('my-activities')}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
          </>
        );

      case 'my-activities':
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <MyActivitiesPage 
                onBack={handleBackToHome}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'my-participations':
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <MyParticipationsPage 
                onBack={handleBackToHome}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
          {organizerDetailsDialog}
          </>
        );

      case 'groups':
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <GroupManagementPage 
                onBack={handleBackToHome}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
          {organizerDetailsDialog}
          </>
        );

      case 'notifications':
        console.log('📱 Rendering notifications view, isMobile:', isMobile);
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              {isMobile ? (
                // Mobile: Full page layout
                <Box sx={{ p: 2 }}>
                  <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
                    📬 Notifications
                  </Typography>
                  <NotificationCenter 
                    open={true} 
                    onClose={() => {
                      console.log('📱 Mobile notification center onClose called, isNavigating state:', isNavigatingFromNotification, 'ref:', navigationFlagRef.current);
                      // Don't close if navigation is in progress (check both state and ref)
                      if (!isNavigatingFromNotification && !navigationFlagRef.current) {
                        console.log('📱 Mobile: Navigating to home');
                        setCurrentView('home');
                      } else {
                        console.log('📱 Mobile: Navigation in progress, NOT going to home');
                      }
                    }} 
                  />
                </Box>
              ) : (
                // Desktop: Better integrated layout
                <Box sx={{ 
                  maxWidth: '1200px', 
                  mx: 'auto', 
                  px: 3,
                  display: 'flex',
                  gap: 3,
                  minHeight: 'calc(100vh - 120px)'
                }}>
                  {/* Desktop Notification Panel */}
                  <Box sx={{ 
                    flex: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 1,
                    overflow: 'hidden'
                  }}>
                    <NotificationCenter 
                      open={true} 
                      onClose={() => {
                        console.log('🖥️ Desktop notification center onClose called, isNavigating state:', isNavigatingFromNotification, 'ref:', navigationFlagRef.current);
                        // Don't close if navigation is in progress (check both state and ref)
                        if (!isNavigatingFromNotification && !navigationFlagRef.current) {
                          console.log('🖥️ Desktop: Navigating to home');
                          setCurrentView('home');
                        } else {
                          console.log('🖥️ Desktop: Navigation in progress, NOT going to home');
                        }
                      }}
                      desktopMode={true}
                    />
                  </Box>
                  
                  {/* Desktop Sidebar with Quick Actions */}
                  <Box sx={{ 
                    width: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    <Box sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: 1,
                      p: 3
                    }}>
                      <Typography variant="h6" gutterBottom>
                        📬 Notifications
                      </Typography>
                      <Typography variant="body2" color="textSecondary" paragraph>
                        Stay updated with your activities, invitations, and messages.
                      </Typography>
                      <Button 
                        variant="outlined" 
                        fullWidth 
                        onClick={() => setCurrentView('home')}
                      >
                        Back to Home
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
            {organizerDetailsDialog}
          </>
        );

      case 'pricing':
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <PricingPage 
                onBack={handleBackToHome} 
                onLogin={handleLogin}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
          {organizerDetailsDialog}
          </>
        );

      case 'profile':
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <ProfilePage 
                onViewPricing={() => setCurrentView('pricing')}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
          {organizerDetailsDialog}
          </>
        );

      // Chat disabled
      // case 'chat':
      //   return (
      //     <>
      //       <Box sx={{ 
      //         pt: { xs: 4, md: 10 }, 
      //         pb: { xs: 12, md: 3 },
      //         minHeight: '100vh',
      //         width: '100%',
      //         overflowX: 'hidden'
      //       }}>
      //         <ChatPage onBack={handleBackToHome} />
      //       </Box>
      //       {renderNavigation()}
      //     </>
      //   );

      default:
        return (
          <>
            <Box sx={{ 
              pt: { xs: 4, md: 10 }, 
              pb: { xs: 12, md: 3 },
              minHeight: '100vh',
              bgcolor: 'background.default',
              width: '100%',
              overflowX: 'hidden'
            }}>
              <PersonalizedActivityList
                onCategorySelect={handleCategorySelect}
                onOrganizerSelect={handleOrganizerSelect}
                onActivitySelect={handleActivitySelect}
                onCountrySelect={handleCountrySelect}
                onCalendarView={handleCalendarView}
                onNotificationsView={handleNotificationsView}
                onMyActivitiesView={() => setCurrentView('my-activities')}
              />
            </Box>
            <AuthenticatedAppFooter onNavigateToPricing={handleFooterPricingNavigation} />
            {renderNavigation()}
          {organizerDetailsDialog}
          </>
        );
    }
  };

  return (
    <NotificationProvider 
      onNavigateToActivity={handleNotificationNavigation}
    >
      <>
        <LazyLoadErrorBoundary>
          <Suspense fallback={<LoadingFallback message="Loading application..." />}>
            {renderMainContent()}
          </Suspense>
        </LazyLoadErrorBoundary>
        
        {/* Create Activity Dialog */}
        <CreateActivityDialog
          open={showCreateActivityDialog}
          onClose={() => {
            setShowCreateActivityDialog(false);
            // Track create activity dialog closure
            trackEvent('create_activity_dialog_closed', 'engagement', 'dialog_dismissed');
            trackUserAction('create_activity_dialog_dismissed', {
              source: currentView,
              timestamp: new Date().toISOString()
            });
          }}
          onSuccess={handleActivityCreated}
          onViewPricing={() => setCurrentView('pricing')}
        />
        
        {/* Plan Activation Dialog */}
        {selectedPlanForActivation && (
          <PlanActivationDialog
            open={showPlanActivationDialog}
            onClose={handleClosePlanActivationDialog}
            planSelection={selectedPlanForActivation}
            onActivationSuccess={handlePlanActivationSuccess}
          />
        )}
        
        {/* Smart Notification Dialog - Will be moved to ActivityDetailPage */}
        {/* Notification dialogs will be shown on Activity Detail page when coming from notifications */}
      </>
    </NotificationProvider>
  );
};

// Maintenance Page Component
const MaintenancePage: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            textAlign: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
          }}
        >
          {/* Logo/Icon */}
          <Box sx={{ mb: 3 }}>
            <Box
              component="span"
              sx={{
                fontSize: '4rem',
                display: 'block',
              }}
            >
              🚧
            </Box>
          </Box>

          {/* Main Heading */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#333',
              mb: 2,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
            }}
          >
            Website in Entwicklung
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              mb: 4,
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.7,
            }}
          >
            Diese Website befindet sich derzeit in der Entwicklung und wird in Kürze verfügbar sein.
          </Typography>

          {/* Notice Box */}
          <Box
            sx={{
              bgcolor: '#f5f5f5',
              borderRadius: 2,
              p: 3,
              mb: 3,
              borderLeft: '4px solid #667eea',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#555',
                fontStyle: 'italic',
                lineHeight: 1.6,
              }}
            >
              <strong>Hinweis:</strong> Wir arbeiten daran, Ihnen die beste Erfahrung zu bieten. 
              Bitte besuchen Sie uns in den kommenden Wochen erneut.
            </Typography>
          </Box>

          {/* Thank you message */}
          <Typography
            variant="body2"
            sx={{
              color: '#888',
              fontWeight: 500,
            }}
          >
            Vielen Dank für Ihr Verständnis.
          </Typography>

          {/* TreffPunkts branding */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#aaa',
                fontSize: '0.85rem',
              }}
            >
              TreffPunkts
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

function App() {
  // Show maintenance page if maintenance mode is enabled
  if (MAINTENANCE_MODE) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MaintenancePage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AccessibilityProvider>
        <AuthProvider>
          <UserProfileProvider>
            <RequestCacheProvider>
              <CategoriesProvider>
                <PricingProvider>
                  <LocationProvider>
                    {/* Chat disabled */}
                    <ChatProvider>
                      {/* WCAG 2.4.1 - Skip Navigation Links */}
                      <SkipNavigation />
                      <AppContent />
                      {/* GDPR Cookie Consent Banner */}
                      <CookieConsentBanner />
                    </ChatProvider>
                  </LocationProvider>
                </PricingProvider>
              </CategoriesProvider>
            </RequestCacheProvider>
          </UserProfileProvider>
        </AuthProvider>
      </AccessibilityProvider>
    </ThemeProvider>
  );
}

export default App;
